import { useCallback, useEffect, useMemo, useState } from 'react';

import { DateTime } from 'luxon';

import { StyledDateInput } from '../../../../components';
import { useFilters } from '../../../../services/contexts/FiltersContext';
import { useUserDocument } from '../../../../services/contexts/UserDocumentContext';
import { useVendasResumo } from '../../../../services/hooks/useVendasResumo';
import { CompleteProdutType } from '../../../../utils/CompleteProdutType';
import { ThityDaysAgo } from '../../../../utils/ThityDaysAgo';
import { ThityDaysFromNow } from '../../../../utils/ThityDaysFromNow';
import { InputDescription } from '../../components/InputDescription';
import { PieChartCard } from './components/PieChartCard';
import { Container } from './styles';

export const Dashboard = () => {
  const { documentSelected } = useUserDocument();
  const { initialDate, setInitialDate, finalDate, setFinalDate } = useFilters();

  const docto = useMemo(() => documentSelected as string, [documentSelected]);

  const now = DateTime.now()
    .setLocale('pt-br')
    .toLocaleString(DateTime.DATE_HUGE);

  const today = new Date();

  const [params, setParams] = useState({
    docto: '',
    dtInicial: initialDate
      ?.toLocaleDateString('pt-BR')
      .split('/')
      .reverse()
      .join('-'),
    dtFinal: finalDate
      ?.toLocaleDateString('pt-BR')
      .split('/')
      .reverse()
      .join('-'),
  });

  const { data, isLoading, isFetching, error, refetch } = useVendasResumo(
    params,
    'dashboard'
  );

  useEffect(() => {
    if (docto) {
      setParams((previousState) => ({ ...previousState, docto: docto }));
    }
  }, [docto]);

  useEffect(() => {
    setFinalDate(today);
    setInitialDate(ThityDaysAgo(today));
  }, [])

  const dataByFlag = useMemo(() => {
    const bandeiras = [...new Set(data?.flatMap((resumo) => resumo.bandeira))];

    const resumos = bandeiras?.map((bandeira) =>
      data?.filter((venda) => venda.bandeira === bandeira)
    );

    const vendasResumoData = resumos?.map((venda) =>
      venda?.reduce(
        (acc, index) => ({
          label: index.bandeira,
          value: acc.value + index.qtdTransacao || 0,
        }),
        { label: '', value: 0 }
      )
    );

    const sort = vendasResumoData.sort((a, b) => {
      if (a && b !== undefined && a?.value > b?.value) {
        return -1;
      }
      if (a && b !== undefined && a?.value < b?.value) {
        return 1;
      }
      // a must be equal to b
      return 0;
    });

    return sort || [{ label: '', quantity: 0 }];
  }, [data]);

  const dataByPaymentMethod = useMemo(() => {
    const tipoProd = [...new Set(data?.flatMap((resumo) => resumo.tipoProd))];

    const resumos = tipoProd?.map((tipo) =>
      data?.filter((venda) => venda.tipoProd === tipo)
    );

    const vendasResumoData = resumos?.map((venda) =>
      venda?.reduce(
        (acc, index) => ({
          label: CompleteProdutType(index.tipoProd),
          value: acc.value + index.qtdTransacao || 0,
        }),
        { label: '', value: 0 }
      )
    );

    const sort = vendasResumoData.sort((a, b) => {
      if (a && b !== undefined && a?.value > b?.value) {
        return -1;
      }
      if (a && b !== undefined && a?.value < b?.value) {
        return 1;
      }
      // a must be equal to b
      return 0;
    });

    return sort || [{ label: '', quantity: 0 }];
  }, [data]);

  const handleFilter = useCallback(() => {
    const newParams = {
      docto: docto,
      dtInicial: initialDate
        ?.toLocaleDateString('pt-BR')
        .split('/')
        .reverse()
        .join('-'),
      dtFinal: finalDate
        ?.toLocaleDateString('pt-BR')
        .split('/')
        .reverse()
        .join('-'),
    };

    setParams(newParams);
  }, [initialDate, finalDate, docto]);

  useEffect(() => {
    return handleFilter();
  }, [handleFilter]);

  return (
    <Container>
      {/* {modal && (
        <StatusModal
          text="Prezado Cliente,
          <br/>
          <br/>
          Nosso sistema está atualmente em manutenção. Pedimos desculpas pela inconveniência. Recomendamos que tente novamente mais tarde.
          <br/>
          <br/>
          Qualquer dúvida, estamos à disposição pelo Whatsapp (11) 94785-6815.
          <br/>
          <br/>
          Equipe Zignet"
          setModal={setModal}
          type="success"
          key={1}
          innerHtml={true}
        />
      )} */}
      <p className="date">{now}</p>
      <div className="receivableContainer">
        <p>
          Recebível referente ao período:{' '}
          <span>
            {data
              ?.reduce((acc, value) => acc + value.vlTransacao, 0)
              .toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 2,
              })}
          </span>
        </p>
      </div>

      <div className="filtersContainer">
        <div>
          <InputDescription placeholder="Data inicial" />
          <StyledDateInput
            value={initialDate}
            onChange={setInitialDate}
            placeholder="Data inicial"
            minDate={finalDate ? ThityDaysAgo(finalDate) : undefined}
            maxDate={finalDate || undefined}
          />
        </div>

        <div>
          <InputDescription placeholder="Data final" />
          <StyledDateInput
            value={finalDate}
            onChange={setFinalDate}
            placeholder="Data final"
            minDate={initialDate || undefined}
            maxDate={initialDate ? ThityDaysFromNow(initialDate) : today}
          />
        </div>
        {/* <StyledButton
          text="Filtrar"
          iconPosition="right"
          icon={<FaFilter />}
          onClick={handleFilter}
        /> */}
      </div>

      <div className="chartsContainer">
        <PieChartCard
          data={dataByFlag}
          isLoading={isLoading}
          isFetching={isFetching}
          error={error}
          title="Vendas"
        />
        <PieChartCard
          data={dataByPaymentMethod}
          isLoading={isLoading}
          isFetching={isFetching}
          error={error}
          title="Vendas por Método de Pagamento"
        />
      </div>
    </Container>
  );
};
